import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import Pagination from "../Pagination/Pagination";
import {
  fetchSocialMediaData,
  fetchSocialMediaDataByUser,
  fetchSocialMediaDataByUserAndPlatform
} from "../../actions/socialMediaData";
import NoData from "../common/actualNoData";
import ScanMessage from "../common/scanningMessage";
import AccessPage from "../Shared/accessPage";
import { Tooltip as ReactTooltip } from "react-tooltip";
import ImageModal from "../common/ImageModal";

const SocialMediaData = () => {
  const { orgId } = useParams();
  const dispatch = useDispatch();
  const myRef = useRef(null);

  const id = useSelector(state => {
    if (orgId) {
      return orgId;
    } else {
      return state?.summary?.localClient?._id || state?.summary?.clients?.[0]?._id;
    }
  });

  const auth = useSelector((state) => state?.auth?.user);
  const socialMediaData = useSelector((state) => state?.socialMediaData);
  const client = useSelector(
    (state) => state?.summary?.localClient || state?.summary?.clients?.[0]
  );

  // Data for statistics
  const scanCompleted = socialMediaData?.scanCompleted !== false;

  // States - simplified to only include necessary state variables
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [matchedKeywordsOnly, setMatchedKeywordsOnly] = useState(""); // "" for all, "true" for matched keywords only
  const [totalItems, setTotalItems] = useState(0);

  // Modal states
  const [isPostDataModal, setPostDataModal] = useState(false);
  const [scrapedData, setScrapedData] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalSrc, setImageModalSrc] = useState("");

  // Set total items when data changes
  useEffect(() => {
    setTotalItems(socialMediaData?.total || 0);
  }, [socialMediaData?.total]);

  // Main data fetching effect - simplified to only use matchedKeywordsOnly filter
  useEffect(() => {
    if (id) {
      dispatch(fetchSocialMediaData(id, page, limit, matchedKeywordsOnly));
    }
  }, [id, page, limit, matchedKeywordsOnly, dispatch]);

  // All date and search related functions removed

  const renderTable = () => {
    if (socialMediaData.loading) {
      return <ScanMessage />;
    }

    if (socialMediaData.error) {
      return (
        <div className="alert alert-danger">
          {socialMediaData?.error?.userMessage || "An error occurred while loading data"}
        </div>
      );
    }

    // Handle Redux state structure
    const posts = socialMediaData?.data || [];

    if (!posts || posts.length === 0) {
      return <NoData />;
    }

    return (
      <table className="table table-custom spacing5 mb-0">
        <thead>
          <tr>
            <th>Username</th>
            <th>Platform</th>
            <th>Post Content</th>
            <th>Posted Date</th>
            <th>Engagement</th>
            <th>Status</th>
            <th>Sentiment</th>
            <th>Keywords</th>
          </tr>
        </thead>
        <tbody>
          {posts.map((item, index) => (
            <tr key={item._id || index}>
              <td>
                <div className="d-flex align-items-center">
                  {item.scrapedData?.user?.profilePic && (
                    <img
                      src={item.scrapedData.user.profilePic}
                      alt="Profile"
                      className="rounded-circle me-2 image-clickable"
                      style={{ width: "30px", height: "30px" }}
                      onClick={() => {
                        setImageModalSrc(item.scrapedData.user.profilePic);
                        setImageModalOpen(true);
                      }}
                    />
                  )}
                  <div>
                    <strong>{item.usernameId?.userName || item.scrapedData?.user?.name || "N/A"}</strong>
                    {item.scrapedData?.user?.name && item.usernameId?.userName && (
                      <div className="text-muted small">
                        @{item.usernameId.userName}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td>
                <span className={`badge badge-${getPlatformColor(item.platform)}`}>
                  {item?.platform || "N/A"}
                </span>
              </td>
              <td>
                <div className="text-truncate" style={{ maxWidth: "300px" }}>
                  {item?.text || item?.scrapedData?.text || "N/A"}
                </div>
                <div className="mt-1">
                  {item.scrapedData && (
                    <button
                      onClick={() => showPostDataModal(item.scrapedData, item)}
                      className="btn btn-sm btn-primary mr-2"
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "4px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 5px rgba(0, 0, 0, 0.15)"
                      }}
                      data-toggle="tooltip"
                      title="View Details"
                    >
                      <i className="fa fa-eye"></i>
                    </button>
                  )}
                  {item.scrapedData?.url && (
                    <a
                      href={item.scrapedData.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-primary"
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "4px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 5px rgba(0, 0, 0, 0.15)"
                      }}
                      data-toggle="tooltip"
                      title="View Original Post"
                    >
                      <i className="fa fa-external-link"></i>
                    </a>
                  )}
                </div>
              </td>
              <td>
                {item.scrapedData?.time || item.timestamp ?
                  moment(item.scrapedData?.time || item.timestamp).format("MMM DD, YYYY HH:mm") :
                  "N/A"
                }
              </td>
              <td>
                <div className="d-flex flex-column">
                  {item?.likes !== undefined && <small><i className="fa fa-thumbs-up"></i> {item.likes}</small>}
                  {item?.shares !== undefined && <small><i className="fa fa-share-alt"></i> {item.shares}</small>}
                  {item?.comments !== undefined && <small><i className="fa fa-comment"></i> {item.comments}</small>}
                </div>
              </td>
              <td>
                <span className={`badge badge-${getStatusColor(item?.isActive ? 'active' : 'inactive')}`}>
                  {item?.isActive ? 'Active' : 'Inactive'}
                </span>
                {item?.scrapeStatus && (
                  <div className="mt-1">
                    <span className={`badge badge-${getScrapeStatusColor(item.scrapeStatus)}`}>
                      {item.scrapeStatus}
                    </span>
                  </div>
                )}
              </td>
              <td>
                {item?.sentiment ? (
                  <div className="d-flex flex-column">
                    <span className={`badge badge-${getSentimentColor(item.sentiment.label)} mb-1`}>
                      {item.sentiment.label}
                    </span>
                    <small className="text-muted">
                      Score: {(item.sentiment.score * 100).toFixed(1)}%
                    </small>
                  </div>
                ) : (
                  <span className="text-muted">N/A</span>
                )}
              </td>
              <td>
                {item?.matchedKeywords && item.matchedKeywords.length > 0 ? (
                  <div className="d-flex flex-wrap gap-1">
                    {item.matchedKeywords.map((keyword, idx) => (
                      <span key={idx} className="badge badge-warning small">
                        {keyword}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted">None</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const getPlatformColor = (platform) => {
    const colors = {
      twitter: "primary",
      facebook: "info",
      instagram: "warning",
      linkedin: "success",
      youtube: "danger",
      tiktok: "dark"
    };
    return colors[platform?.toLowerCase()] || "secondary";
  };

  const getStatusColor = (status) => {
    const colors = {
      active: "success",
      inactive: "secondary",
      suspended: "warning",
      banned: "danger"
    };
    return colors[status?.toLowerCase()] || "primary";
  };

  const showPostDataModal = (data, originalItem) => {
    // If originalItem is provided, merge relevant fields
    if (originalItem) {
      setScrapedData({
        ...data,
        parentItem: originalItem,
        text: originalItem.text || data.text
      });
    } else {
      setScrapedData(data);
    }
    setPostDataModal(true);
  };

  const getScrapeStatusColor = (status) => {
    const colors = {
      success: "success",
      failed: "danger",
      pending: "warning",
      error: "danger"
    };
    return colors[status?.toLowerCase()] || "secondary";
  };

  const getSentimentColor = (label) => {
    const colors = {
      positive: "success",
      negative: "danger",
      neutral: "secondary"
    };
    return colors[label?.toLowerCase()] || "secondary";
  };

  const getIconByPlatform = (platform) => {
    const icons = {
      instagram: "fa-instagram",
      facebook: "fa-facebook-square",
      twitter: "fa-twitter",
      linkedin: "fa-linkedin",
      youtube: "fa-youtube-play",
      tiktok: "fa-music"
    };
    return icons[platform?.toLowerCase()] || "fa-globe";
  };

  const getPlatformHexColor = (platform) => {
    const colors = {
      instagram: "#E1306C",
      facebook: "#4267B2",
      twitter: "#1DA1F2",
      linkedin: "#0077B5",
      youtube: "#FF0000",
      tiktok: "#000000"
    };
    return colors[platform?.toLowerCase()] || "#6c757d";
  };

  const getPlatformDisplayName = (platform) => {
    const names = {
      instagram: "Instagram",
      facebook: "Facebook",
      twitter: "Twitter",
      linkedin: "LinkedIn",
      youtube: "YouTube",
      tiktok: "TikTok"
    };
    return names[platform?.toLowerCase()] || "Social Media";
  };

  if (!auth) {
    return <AccessPage />;
  }

  return (
    <>
      <div className="container-fluid">
        <AccessPage></AccessPage>
        <ReactTooltip className="reactTooltipClass" />
        <div className="block-header">
          <div className="row clearfix">
            <div className="col-md-6 col-sm-12">
              <h1 className="welcome-text">Social Media Data</h1>
              <nav aria-label="breadcrumb" className="mt-1">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/" className="welcome-textSmall">Home</Link>
                  </li>
                  <li
                    className="breadcrumb-item active"
                    aria-current="page"
                    style={{ color: "white" }}
                  >
                    Social Media Data
                  </li>
                </ol>
                <br />
              </nav>
            </div>
          </div>
        </div>

        {typeof scanCompleted !== "undefined" && !scanCompleted && (
          <div className="card border-success">
            <ScanMessage />
          </div>
        )}

        <div className="row clearfix">
          <div className="col-lg-12 col-md-12">
            <div className="row clearfix">
              <div className="col-lg-12 col-md-12">

                <div className="card card_tableBox" ref={myRef}>
                  <div className="header">
                    <div className="body">
                      <div className="row">
                        <div className="col-sm-3 mb-2">
                          <label>Blacklisted Keywords</label>
                          <select
                            className="custom-select custom-select-sm form-control form-control-sm"
                            value={matchedKeywordsOnly}
                            onChange={(e) => {
                              setMatchedKeywordsOnly(e.target.value);
                              setPage(1);
                            }}
                          >
                            <option value="">All</option>
                            <option value="true">Blacklisted Keywords: Yes</option>
                          </select>
                        </div>
                      </div>
                      <div className="table-responsive">
                        {renderTable()}
                      </div>
                      <div className="d-flex justify-content-between align-content-center">
                        <div className="dataTables_paginate">
                          {totalItems > 0 && (
                            <Pagination
                              className="pagination-bar"
                              currentPage={page}
                              totalCount={totalItems}
                              pageSize={limit}
                              onPageChange={(page) => setPage(page)}
                            />
                          )}
                        </div>
                        {totalItems > 10 && (
                          <select
                            value={limit}
                            onChange={(e) => {
                              setLimit(parseInt(e.target.value));
                              setPage(1);
                            }}
                            className="form-control limitSelectBox"
                          >
                            <option value={10}>10 per page</option>
                            <option value={25}>25 per page</option>
                            <option value={50}>50 per page</option>
                            <option value={100}>100 per page</option>
                          </select>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Social Media Post Details Modal */}
      <div className={`modal fade Form-Wizard-modal ${isPostDataModal ? "d-block show" : ""}`}>
        <div className="modal-dialog modal-lg" id="postDataModal">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Social Media Post Details</h5>
              <button
                type="button"
                onClick={() => {
                  setPostDataModal(false);
                  setScrapedData(null);
                }}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">
              {scrapedData && (
                <div className="row">
                  {/* Media content - only show if displayUrl is available */}
                  {scrapedData.displayUrl && (
                    <div className="col-md-5 text-center mb-3">
                      <img
                        src={scrapedData.displayUrl}
                        alt={scrapedData.alt || "Social media post"}
                        className="img-fluid rounded mb-3 image-clickable"
                        style={{ maxHeight: '400px' }}
                        onClick={() => {
                          setImageModalSrc(scrapedData.displayUrl);
                          setImageModalOpen(true);
                        }}
                      />
                      <div className="mt-2">
                        <a
                          href={scrapedData.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary btn-sm"
                        >
                          View Original Post
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Video content - only show if videoUrl is available */}
                  {!scrapedData.displayUrl && scrapedData.type === 'Video' && scrapedData.videoUrl && (
                    <div className="col-md-5 text-center mb-3">
                      <video
                        controls
                        className="img-fluid rounded mb-3"
                        style={{ maxHeight: '400px' }}
                      >
                        <source src={scrapedData.videoUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                      <div className="mt-2">
                        <a
                          href={scrapedData.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary btn-sm"
                        >
                          View Original Post
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Post details - adjust column width based on media presence */}
                  <div className={`col-md-${scrapedData.displayUrl || (scrapedData.type === 'Video' && scrapedData.videoUrl) ? '7' : '12'}`}>
                    <div className="post-details">
                      {/* Post Information section */}
                      <div className="mb-4 pb-2 border-bottom">
                        <h5 className="mb-3">Post Information</h5>
                        <div className="d-flex flex-wrap">
                          <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                            <div className="mb-2">
                              <span className="font-weight-bold">Platform</span>
                            </div>
                            <div>
                              {scrapedData.platform || (scrapedData.url?.includes('instagram') ? 'Instagram' :
                                scrapedData.url?.includes('facebook') ? 'Facebook' :
                                  scrapedData.url?.includes('tiktok') ? 'TikTok' : 'Social Media')}
                            </div>
                          </div>

                          <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                            <div className="mb-2">
                              <span className="font-weight-bold">Dimensions</span>
                            </div>
                            <div>
                              {scrapedData.dimensionsWidth && scrapedData.dimensionsHeight ?
                                `${scrapedData.dimensionsWidth}px × ${scrapedData.dimensionsHeight}px` :
                                'N/A'}
                            </div>
                          </div>

                          <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                            <div className="mb-2">
                              <span className="font-weight-bold">Type</span>
                            </div>
                            <div>{scrapedData.type || "Image"}</div>
                          </div>

                          <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                            <div className="mb-2">
                              <span className="font-weight-bold">Comments Disabled</span>
                            </div>
                            <div>{scrapedData.isCommentsDisabled ? 'Yes' : 'No'}</div>
                          </div>
                        </div>

                        <div className="d-flex flex-wrap">
                          <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                            <div className="mb-2">
                              <span className="font-weight-bold">URL</span>
                            </div>
                            <div>
                              <a href={scrapedData.url} target="_blank" rel="noopener noreferrer">View</a>
                            </div>
                          </div>

                          <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                            <div className="mb-2">
                              <span className="font-weight-bold">Posted On</span>
                            </div>
                            <div>{moment(scrapedData.timestamp).format('MMM D, YYYY')}</div>
                          </div>

                          <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                            <div className="mb-2">
                              <span className="font-weight-bold">ID</span>
                            </div>
                            <div>{scrapedData.id || 'N/A'}</div>
                          </div>

                          <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                            <div className="mb-2">
                              <span className="font-weight-bold">Key</span>
                            </div>
                            <div>{scrapedData.key || 'N/A'}</div>
                          </div>

                          <div className="mr-4 mb-3" style={{ minWidth: '180px', width: '100%' }}>
                            <div className="mb-2">
                              <span className="font-weight-bold">Description</span>
                            </div>
                            <div className="border p-2" style={{ whiteSpace: 'pre-wrap', maxHeight: '200px', overflowY: 'auto' }}>
                              {scrapedData.text || (scrapedData.parentItem && scrapedData.parentItem.text) || 'N/A'}
                            </div>
                          </div>

                          <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                            <div className="mb-2">
                              <span className="font-weight-bold">Short Code</span>
                            </div>
                            <div>{scrapedData.shortCode || 'N/A'}</div>
                          </div>
                        </div>

                        <div className="d-flex flex-wrap">
                          <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                            <div className="mb-2">
                              <span className="font-weight-bold">Sponsored</span>
                            </div>
                            <div>{scrapedData.isSponsored ? 'Yes' : 'No'}</div>
                          </div>

                          {scrapedData.productType && (
                            <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                              <div className="mb-2">
                                <span className="font-weight-bold">Product Type</span>
                              </div>
                              <div>{scrapedData.productType}</div>
                            </div>
                          )}

                          {scrapedData.videoDuration !== undefined && (
                            <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                              <div className="mb-2">
                                <span className="font-weight-bold">Video Duration</span>
                              </div>
                              <div>{scrapedData.videoDuration}s</div>
                            </div>
                          )}
                        </div>

                        {scrapedData.caption && (
                          <div className="mt-3">
                            <div className="mb-2">
                              <span className="font-weight-bold">Caption</span>
                            </div>
                            <div className="border p-2" style={{ whiteSpace: 'pre-wrap', maxHeight: '100px', overflowY: 'auto' }}>
                              {scrapedData.caption}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Engagement Stats section */}
                      <div className="mb-4 pb-2 border-bottom">
                        <h5 className="mb-3">Engagement Stats</h5>
                        <div className="d-flex flex-wrap">
                          <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                            <div className="mb-2">
                              <span className="font-weight-bold">Likes</span>
                            </div>
                            <div>{scrapedData.likesCount || scrapedData.like_count || 0}</div>
                          </div>

                          <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                            <div className="mb-2">
                              <span className="font-weight-bold">Comments</span>
                            </div>
                            <div>{scrapedData.commentsCount || scrapedData.comment_count || 0}</div>
                          </div>

                          <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                            <div className="mb-2">
                              <span className="font-weight-bold">Views</span>
                            </div>
                            <div>{scrapedData.videoViewCount || scrapedData.view_count || 0}</div>
                          </div>

                          {scrapedData.parentItem?.sentiment && (
                            <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                              <div className="mb-2">
                                <span className="font-weight-bold">Sentiment</span>
                              </div>
                              <div>
                                <span className={`badge badge-${getSentimentColor(scrapedData.parentItem.sentiment.label)} mb-1`}>
                                  {scrapedData.parentItem.sentiment.label}
                                </span>
                                <div className="mt-1">
                                  <small className="text-muted">
                                    Score: {(scrapedData.parentItem.sentiment.score * 100).toFixed(1)}%
                                  </small>
                                </div>
                              </div>
                            </div>
                          )}

                          {scrapedData.videoPlayCount !== undefined && (
                            <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                              <div className="mb-2">
                                <span className="font-weight-bold">Video Plays</span>
                              </div>
                              <div>{scrapedData.videoPlayCount}</div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Owner Information */}
                      <div className="mb-4 pb-2 border-bottom">
                        <h5 className="mb-3">Owner Information</h5>
                        <div className="d-flex flex-wrap">
                          <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                            <div className="mb-2">
                              <span className="font-weight-bold">Full Name</span>
                            </div>
                            <div>{scrapedData.ownerFullName || 'N/A'}</div>
                          </div>

                          <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                            <div className="mb-2">
                              <span className="font-weight-bold">Username</span>
                            </div>
                            <div>
                              {scrapedData.ownerUsername && (
                                <a href={`https://www.instagram.com/${scrapedData.ownerUsername}`}
                                  target="_blank" rel="noopener noreferrer">
                                  @{scrapedData.ownerUsername}
                                </a>
                              )}
                              {!scrapedData.ownerUsername && 'N/A'}
                            </div>
                          </div>

                          <div className="mr-4 mb-3" style={{ minWidth: '180px' }}>
                            <div className="mb-2">
                              <span className="font-weight-bold">Owner ID</span>
                            </div>
                            <div>{scrapedData.ownerId || 'N/A'}</div>
                          </div>
                        </div>
                      </div>

                      {/* Hashtags & Mentions */}
                      {(scrapedData.hashtags?.length > 0 || scrapedData.mentions?.length > 0) && (
                        <div className="mb-4 pb-2 border-bottom">
                          {scrapedData.hashtags?.length > 0 && (
                            <div className="mb-3">
                              <h5 className="mb-2">Hashtags</h5>
                              <div>
                                {scrapedData.hashtags.map((tag, index) => (
                                  <span key={index} className="badge badge-info text-white m-1">
                                    #{tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {scrapedData.mentions?.length > 0 && (
                            <div className="mb-3">
                              <h5 className="mb-2">Mentions</h5>
                              <div>
                                {scrapedData.mentions.map((mention, index) => (
                                  <span key={index} className="badge badge-primary text-white m-1">
                                    @{mention}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Other Content */}
                      {(scrapedData.alt || scrapedData.musicInfo) && (
                        <div className="mb-4 pb-2 border-bottom">
                          {scrapedData.alt && (
                            <div className="mb-3">
                              <h5 className="mb-2">Image Alt Text</h5>
                              <div className="border p-2 bg-light">
                                <small>{scrapedData.alt}</small>
                              </div>
                            </div>
                          )}

                          {scrapedData.musicInfo && (
                            <div className="mb-3">
                              <h5 className="mb-2">Music Information</h5>
                              <div className="d-flex flex-wrap">
                                <div className="mr-4 mb-2" style={{ minWidth: '180px' }}>
                                  <div><span className="font-weight-bold">Artist:</span> {scrapedData.musicInfo.artist_name}</div>
                                </div>
                                <div className="mr-4 mb-2" style={{ minWidth: '180px' }}>
                                  <div><span className="font-weight-bold">Song:</span> {scrapedData.musicInfo.song_name}</div>
                                </div>
                                <div className="mr-4 mb-2" style={{ minWidth: '180px' }}>
                                  <div>
                                    <span className="font-weight-bold">Original Audio:</span>
                                    {scrapedData.musicInfo.uses_original_audio ? ' Yes' : ' No'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Comments & Child Posts */}
                      {(scrapedData.latestComments?.length > 0 || scrapedData.childPosts?.length > 0) && (
                        <div>
                          {scrapedData.latestComments?.length > 0 && (
                            <div className="mb-3">
                              <h5 className="mb-2">Latest Comments</h5>
                              <div className="border rounded">
                                {scrapedData.latestComments.map((comment, index) => (
                                  <div key={index} className="p-2 border-bottom">
                                    <div>
                                      <strong>{comment.username || 'User'}</strong>
                                      {comment.timestamp && (
                                        <small className="text-muted ml-2">
                                          {moment(comment.timestamp).fromNow()}
                                        </small>
                                      )}
                                    </div>
                                    <div className="mt-1 small">{comment.text}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {scrapedData.childPosts?.length > 0 && (
                            <div className="mb-3">
                              <h5 className="mb-2">Child Posts ({scrapedData.childPosts.length})</h5>
                              <div className="row">
                                {scrapedData.childPosts.map((childPost, index) => (
                                  <div key={index} className="col-4 mb-2">
                                    {childPost.displayUrl && (
                                      <img
                                        src={childPost.displayUrl}
                                        alt="Child post"
                                        className="img-fluid rounded image-clickable"
                                        style={{ height: '80px', objectFit: 'cover', width: '100%' }}
                                        onClick={() => {
                                          setImageModalSrc(childPost.displayUrl);
                                          setImageModalOpen(true);
                                        }}
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {!scrapedData && (
                <div className="text-center py-5">
                  <div className="py-5">
                    <i className="fa fa-image fa-4x text-muted mb-3"></i>
                    <h5 className="text-muted">No post data to display</h5>
                    <p className="text-muted">Select a post to view its details</p>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setPostDataModal(false);
                  setScrapedData(null);
                }}
              >
                Close
              </button>
              {scrapedData && scrapedData.url && (
                <a
                  href={scrapedData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  View Original
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <ImageModal
        isOpen={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        imageSrc={imageModalSrc}
      />
    </>
  );
};

export default SocialMediaData;

